// IndexedDB-backed store for large Action-panel media (audio/video).
//
// Why: the Action panel persists its config into sessionStorage under
// 'allDetails', which has a ~5MB quota shared across the WHOLE program. A single
// 2-3MB audio/video clip encoded as a base64 data URL blows that quota, so the
// save fails with QuotaExceededError. Images stay inline (they are downscaled to
// a small JPEG first), but audio/video blobs live here instead, and allDetails
// keeps only a lightweight string key. IndexedDB's quota is orders of magnitude
// larger (hundreds of MB+), so multi-MB clips save fine.

const DB_NAME = 'plode_ai_media'
const STORE = 'media'
const VERSION = 1

let dbPromise = null

const openDB = () => {
    if (dbPromise) return dbPromise
    dbPromise = new Promise((resolve, reject) => {
        const req = indexedDB.open(DB_NAME, VERSION)
        req.onupgradeneeded = () => {
            const db = req.result
            if (!db.objectStoreNames.contains(STORE))
                db.createObjectStore(STORE)
        }
        req.onsuccess = () => resolve(req.result)
        req.onerror = () => reject(req.error)
    })
    return dbPromise
}

const run = async (mode, op) => {
    const db = await openDB()
    return new Promise((resolve, reject) => {
        const t = db.transaction(STORE, mode)
        const store = t.objectStore(STORE)
        const req = op(store)
        let result
        if (req) req.onsuccess = () => (result = req.result)
        t.oncomplete = () => resolve(result)
        t.onerror = () => reject(t.error)
        t.onabort = () => reject(t.error)
    })
}

let seq = 0

// A stable, unique key for one picked file (survives action reorder/removal).
export const newMediaKey = (nodeId) => {
    seq += 1
    return `m_${nodeId}_${Date.now()}_${seq}`
}

export const putMedia = async (key, blob) => {
    await run('readwrite', (store) => store.put(blob, key))
    return key
}

export const getMediaBlob = async (key) => {
    if (!key) return null
    return run('readonly', (store) => store.get(key))
}

// Resolve a stored blob to an object URL (caller revokes when done). Null if
// the key is missing or the blob was evicted.
export const getMediaURL = async (key) => {
    const blob = await getMediaBlob(key)
    return blob ? URL.createObjectURL(blob) : null
}

export const deleteMedia = async (key) => {
    if (!key) return
    try {
        await run('readwrite', (store) => store.delete(key))
    } catch (e) {
        /* best-effort cleanup */
    }
}
